import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Check, Calendar, Clock, MapPin, Phone, User, Users, ArrowLeft, Clock as ClockIcon } from 'lucide-react';
import { format } from 'date-fns';
import { ServiceType } from '../types';

interface BookingDetails {
  service: ServiceType;
  date: Date;
  time: string;
  fullName: string;
  phone: string;
  email: string;
  partySize: number;
  notes?: string;
}

const ConfirmationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const booking = location.state as BookingDetails;

  useEffect(() => {
    // If no booking data is available, redirect to the booking page
    if (!booking) {
      navigate('/booking');
    }
    
    // In a real app, this is where you would:
    // 1. Save the booking to your database
    // 2. Send confirmation emails
    // 3. Add the event to Google Calendar
    
  }, [booking, navigate]);

  if (!booking) {
    return null; // This will redirect in the useEffect
  }

  const formatDate = (date: Date) => {
    return format(new Date(date), 'EEEE, MMMM d, yyyy');
  };

  return (
    <div className="pt-24 pb-16">
      <div className="container-custom">
        <div className="max-w-2xl mx-auto text-center mb-12 animate-slide-up">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-warning-100 dark:bg-warning-900/30 rounded-full mb-6">
            <ClockIcon className="w-8 h-8 text-warning-600 dark:text-warning-400" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Booking Under Review</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Thank you for booking with Plymouth Braider. 
            Confirmation will be sent to your email when booking is confirmed.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="card p-8 mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <h2 className="text-2xl font-bold mb-6">Appointment Details</h2>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-primary-600 mr-4 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Date</p>
                  <p className="font-medium">{formatDate(booking.date)}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Clock className="w-5 h-5 text-primary-600 mr-4 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Time</p>
                  <p className="font-medium">{booking.time}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <User className="w-5 h-5 text-primary-600 mr-4 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Service</p>
                  <p className="font-medium">{booking.service.name}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Users className="w-5 h-5 text-primary-600 mr-4 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Number of People</p>
                  <p className="font-medium">{booking.partySize}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-primary-600 mr-4 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Location</p>
                  <p className="font-medium">Mutley, Plymouth, UK</p>
                </div>
              </div>
            </div>
            
            <hr className="my-6 border-gray-200 dark:border-gray-700" />
            
            <h3 className="text-lg font-semibold mb-4">Your Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                  <p className="font-medium">{booking.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Phone</p>
                  <p className="font-medium">{booking.phone}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                <p className="font-medium">{booking.email}</p>
              </div>
              {booking.notes && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Additional Notes</p>
                  <p>{booking.notes}</p>
                </div>
              )}
            </div>
          </div>

          <div className="card p-8 mb-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <h2 className="text-xl font-bold mb-4">What's Next?</h2>
            <ul className="space-y-4">
              <li className="flex items-start">
                <ClockIcon className="w-5 h-5 text-warning-600 mr-3 mt-0.5" />
                <span>Your booking is currently under review by our team.</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-success-600 mr-3 mt-0.5" />
                <span>You'll receive a confirmation email once your booking is approved.</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-success-600 mr-3 mt-0.5" />
                <span>We'll send a reminder 24 hours before your appointment.</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-success-600 mr-3 mt-0.5" />
                <span>Please arrive 10 minutes before your scheduled time.</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-success-600 mr-3 mt-0.5" />
                <span>If you need to reschedule, please call us at least 24 hours in advance.</span>
              </li>
            </ul>
          </div>

          <div className="text-center animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <Link to="/" className="btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;